import {
  getStepOneItems,
  stepOneFormSchema,
  type StepOneFormSchema,
} from '@/components/tripBookingPage/datedForm/steps/stepOne/stepOneForm';
import {
  type AddressSchema,
  type IAddressFormSelection,
  stepAddressFormSchema,
} from '@/components/tripBookingPage/formItems/address/addressFormItem';
import {
  type AssistiveDeviceSchema,
  getAssistiveDevicesSchema,
} from '@/components/tripBookingPage/formItems/assistiveDevices/assistiveDevices';
import {
  type CompanionSchema,
  getCompanionsSchema,
} from '@/components/tripBookingPage/formItems/companions/companionsSchema';
import {
  getTimeSchema,
  type ITimeValidated,
  type TimeSchema,
} from '@/components/tripBookingPage/formItems/time/timeSelector';
import {
  type BookingFormStepper,
  type BookingFormStepperPropsRaw,
  type BookingFormStepperReturn,
  FormMode,
  type FormStepperItemCallbackOptions,
  type RoundTripValue,
} from '@/components/tripBookingPage/tripBooking';
import stepCompanionsTitles from '@/components/tripBookingPage/helpers/stepCompanionsTitles';
import { U_NO_BREAK_SPACE } from '@/utils/unicodeCharacters';
import { TRIP_BOOKING_TIME_TYPE_INWARD_DEFAULT, TRIP_BOOKING_TIME_TYPE_OUTWARD_DEFAULT } from '@/config';
import {
  type AnyObject,
  type AssistiveDevice,
  type FormItemConfigCallbackData,
  Locale,
  type Nullable,
  type TripType,
} from '@/types';
import { inject, ref } from 'vue';
import {
  type CompanionAssistiveDevice,
  getEmptyCompanions,
} from '@/components/tripBookingPage/formItems/companions/companionsFormItem.config.ts';
import type { ActiveServiceRequest } from '@/components/tripBookingPage/formSubmission/submissionController.ts';

/**
 * Données pour la validation du formulaire de demande de réservation occasionnelle,
 * utilisé pour préparer l'envoi des données au backend
 */
export type IBookingFormDated = {
  /** Déplacement occasionnel ou régulier */
  scheduleType: 'dated';
  /** Valeurs combinées de toutes les étapes du formulaire */
  values: DatedBookingFormValues;
  /** Langue présentement active */
  locale: Locale;
  /** Étape optionnelle, fournie uniquement lors de la validation de formulaire, jamais pour la soumission. */
  step?: DatedStepName;
};

export interface IBookingFormSubmissionBase {
  /** Valeurs combinées de toutes les étapes du formulaire */
  values: DatedBookingFormValues;
}

/**
 * Données pour la soumission du formulaire de demande de réservation occasionnelle et utilisé pour préparer l'envoi
 * des données au backend
 */
export interface IBookingFormSubmissionDated extends IBookingFormSubmissionBase {
  /** Utilisateur confirme l'annulation du déplacement avant sa recréation */
  userConfirmed: boolean;
}

/** Passage des données entre le contrôleur de soumission et le composable API et pour l'ajout d'un nouveau segment */
export interface IBookingFormLegSubmissionDated extends IBookingFormSubmissionBase {
  /** Clé unique du déplacement précédent, où le nouveau segment doit être ajouté */
  previousServiceRequestKey: string;
  /**
   * Métadonnées de modification, transmises lors de la recréation d'un segment supprimé suite à une modification
   * confirmée mais ayant échoué. Permet au builder de payload de ne créer qu'un seul segment, même si le déplacement
   * est de type aller-retour.
   */
  modificationMetadata?: BookingFormState['metadata'];
}

/** Noms associé à chaque étape dans la configuration du formulaire */
export enum DatedStepName {
  TypeAndDate = 'date-deplacement',
  Address = 'adresse',
  Time = 'heure',
  AssistiveDevices = 'aide-a-la-mobilite',
  Companions = 'accompagnateur',
  Review = 'revision',
}

/** Toutes les propriétés et valeurs du formulaire de réservation occasionnelle */
export type DatedBookingFormValues =
  StepOneFormSchema &
  AddressSchema &
  TimeSchema &
  AssistiveDeviceSchema &
  CompanionSchema;

/** Toutes les données nécessaires pour gérer la modification d'une demande de déplacement occasionnelle */
export type BookingFormState = {
  /** Valeurs permettant l'affichage des données d'un déplacement dans le formulaire */
  allFormValues: DatedBookingFormValues;
  /** Données relative à la gestion de l'heure dans l'étape correspondante */
  selectedTime: RoundTripValue<Nullable<ITimeValidated>>;
  /** Liste des équipements sélectionnés par l'utilisateur */
  selectedDevices: RoundTripValue<AssistiveDevice[]>;
  /** Aides à la mobilité pour les accompagnateurs et déjà enregistrés dans le déplacement par un commis */
  selectedCompanionDevices: CompanionAssistiveDevice[];
  /** Détails relatifs à l'adresse d'origine */
  originAddress: IAddressFormSelection;
  /** Détails relatifs à l'adresse de destination */
  destinationAddress: IAddressFormSelection;
  /** Clés uniques relatives à chaque segment du déplacement en cours de modification */
  legKeys: string[];
  /** Métadonnées sur le déplacement modifié et permettant d'altérer le fonctionnement du formulaire  */
  metadata: ServiceRequestMetadata | ServiceRequestLegMetadata | ServiceRequestNewLegMetadata | ServiceRequestDuplicateMetadata;
};

/** Métadonnées communes entre la modification d'un déplacement et la modification d'un segment spécifique */
type FormModificationMetaBase = {
  serviceRequestKey: string;
  legKey: string;
};

/** Métadonnées relatives à l'ajout d'un nouveau segment dans un déplacement existant */
type ServiceRequestNewLegMetadata = FormModificationMetaBase & {
  modificationType: FormMode.LegCreation;
  /**
   * `null` lors de la création d'un segment (l'ordre final est inconnu tant que le backend n'a pas répondu). Peut
   * toutefois mémoriser la position du segment lorsque celle-ci est connue mais que sa clé devient temporairement
   * indisponible (ex.: segment supprimé par le backend puis en cours de recréation), afin de conserver le bon
   * libellé « Déplacement #n ».
   */
  tripIndex: number | null;
};

/**
 * métadonnées calculées durant l'adaptation de la réponse backend pour la modification du segment d'un déplacement et
 * utilisé pour aider à la modification des étapes/libellés d'étapes.
 */
type ServiceRequestLegMetadata = FormModificationMetaBase & {
  /** Type de modification à effectuer:  un déplacement complet ou le segment d'un déplacement */
  modificationType: FormMode.LegModification;
  /** index du segment en cours de modification */
  tripIndex: number;
};

/**
 * métadonnées calculées durant l'adaptation de la réponse backend pour la modification d'un déplacement et
 * utilisé pour aider à la modification des étapes/libellés d'étapes.
 */
type ServiceRequestMetadata = FormModificationMetaBase & {
  /** type de modification: déplacement complet ou segment d'un déplacement */
  modificationType: FormMode.Modification;
  /** index du segment, null lorsqu'il s'agit de la modification d'un déplacement complet */
  tripIndex: null;
};

/** Métadonnées relatives à la duplication d'un déplacement existant */
type ServiceRequestDuplicateMetadata = FormModificationMetaBase & {
  modificationType: FormMode.Duplicate;
  tripIndex: null;
};

export type DatedFormStepsValues = Record<DatedStepName, AnyObject>;

export type DatedFormSteps<Raw extends boolean = false> = BookingFormStepper<DatedStepName, Raw>;

export type DatedFormStepperReturn = BookingFormStepperReturn<DatedStepName>;

/** Informations sur le provider de la composante de chargement des données */
export type DatedFormLoaderContext = {
  addLeg: (currentValues: DatedBookingFormValues, activeServiceRequest: ActiveServiceRequest) => void;
};

/**
 * Valeurs initiales par étape et basé sur les valeurs par défaut de chaque schéma.
 * NOTE: Les valeurs passées en paramètres ne servent qu'à satisfaire les schémas retournées via une fonction.
 */
export const getStepInitialValues = (formValues: DatedBookingFormValues): DatedFormStepsValues => ({
  [DatedStepName.TypeAndDate]: stepOneFormSchema.getDefault(),
  [DatedStepName.Address]: stepAddressFormSchema.getDefault(),
  [DatedStepName.Time]: getTimeSchema(ref(formValues.tripType || 'oneWay'), FormMode.Creation).getDefault(),
  [DatedStepName.AssistiveDevices]: getAssistiveDevicesSchema({ outward: [], inward: [] }, Locale.fr).getDefault(),
  [DatedStepName.Companions]: getCompanionsSchema(0).getDefault(),
  [DatedStepName.Review]: {},
});

export const getAddressStepperItems = (): BookingFormStepperPropsRaw => ({
  title: 'pages.tripBooking.forms.dated.stepTitles.addresses',
  items: [
    {
      name: 'originAddress.address',
      title: 'pages.tripBooking.forms.items.address.originAddress.title',
      messageBoxError: 'pages.tripBooking.forms.items.address.originAddress.validations.required.messageBoxLabel',
    },
    { name: 'originAddress.apartment', title: '' },
    { name: 'originAddress.driverNote', title: 'pages.tripBooking.forms.items.address.driverNote.title.origin' },
    {
      name: 'destinationAddress.address',
      title: 'pages.tripBooking.forms.items.address.destinationAddress.title',
      messageBoxError: 'pages.tripBooking.forms.items.address.destinationAddress.validations.required.messageBoxLabel',
    },
    { name: 'destinationAddress.apartment', title: '' },
    { name: 'destinationAddress.driverNote', title: 'pages.tripBooking.forms.items.address.driverNote.title.destination' },
  ],
});

export const getTimeStepperItems = (): BookingFormStepperPropsRaw => ({
  title: (data: FormItemConfigCallbackData, { t }: FormStepperItemCallbackOptions) =>
    t('pages.tripBooking.forms.dated.stepTitles.stepTime', Number(data.formValues.tripType !== 'oneWay' && data.formMode !== FormMode.LegModification)),
  items: [
    {
      name: 'requestedTime.outward.type',
      title: (titleData: { tripIndex?: number; tripType: TripType }, { t }: FormStepperItemCallbackOptions) => {
        const tripIndex = titleData?.tripIndex ?? 0;
        return titleData.tripType === 'oneWay' || (titleData.tripType === 'roundTrip' && tripIndex === 0)
          ? t('pages.tripBooking.forms.items.timeSelector.outwardLabel')
          : (titleData.tripType === 'roundTrip' && tripIndex === 1
            ? t('pages.tripBooking.forms.items.timeSelector.inwardLabel')
            : t('pages.tripBooking.forms.items.timeSelector.indexedLabel', { tripIndex: tripIndex + 1 })
          );
      },
    },
    {
      name: 'requestedTime.outward.time',
      title: 'pages.tripBooking.forms.items.timePicker.empty.label',
      messageBoxError: (values: TimeSchema, { t }: FormStepperItemCallbackOptions) => {
        return `${t('generic.outward')}:${U_NO_BREAK_SPACE}` +
          t(values.requestedTime.outward.type === 'arrival'
            ? 'pages.tripBooking.forms.items.timePicker.validations.required.arrival'
            : 'pages.tripBooking.forms.items.timePicker.validations.required.departure');
      },
    },
    { name: 'requestedTime.inward.type', title: 'pages.tripBooking.forms.items.timeSelector.inwardLabel' },
    {
      name: 'requestedTime.inward.time',
      title: 'pages.tripBooking.forms.items.timePicker.empty.label',
      messageBoxError: (values: TimeSchema, { t }: FormStepperItemCallbackOptions) => {
        return `${t('generic.inward')}:${U_NO_BREAK_SPACE}` +
          t(values.requestedTime.inward.type === 'arrival'
            ? 'pages.tripBooking.forms.items.timePicker.validations.required.arrival'
            : 'pages.tripBooking.forms.items.timePicker.validations.required.departure');
      },
    },
  ],
});

export const getDevicesStepperItems = (): BookingFormStepperPropsRaw => ({
  title: 'pages.tripBooking.forms.dated.stepTitles.assistiveDevices',
  items: [
    {
      name: 'assistiveDevices.outward',
      title: (count: number, { t }: FormStepperItemCallbackOptions) =>
        t('pages.tripBooking.forms.items.assistiveDevices.outward.label', count),
    },
    {
      name: 'assistiveDevices.inward',
      title: (count: number, { t }: FormStepperItemCallbackOptions) =>
        t('pages.tripBooking.forms.items.assistiveDevices.inward.label', count),
    },
  ],
});

export const getCompanionsStepperItems = (): BookingFormStepperPropsRaw => ({
  title: 'pages.tripBooking.forms.dated.stepTitles.stepCompanions',
  items: [
    {
      name: 'companions.outward.children',
      title: stepCompanionsTitles.getChildrenCompanionsLabel,
      messageBoxError: function(_, { t }: FormStepperItemCallbackOptions) {
        return `${t('generic.outward')}:${U_NO_BREAK_SPACE}` + this.title;
      },
    },
    {
      name: 'companions.outward.parents',
      title: stepCompanionsTitles.getParentCompanionsLabel,
      messageBoxError: function(_, { t }: FormStepperItemCallbackOptions) {
        return `${t('generic.outward')}:${U_NO_BREAK_SPACE}` + this.title;
      },
    },
    {
      name: 'companions.outward.travelling',
      title: stepCompanionsTitles.getTravellingCompanionsLabel,
      messageBoxError: function(_, { t }: FormStepperItemCallbackOptions) {
        return `${t('generic.outward')}:${U_NO_BREAK_SPACE}` + this.title;
      },
    },
    {
      name: 'companions.inward.children',
      title: stepCompanionsTitles.getChildrenCompanionsLabel,
      messageBoxError: function(_, { t }: FormStepperItemCallbackOptions) {
        return `${t('generic.inward')}:${U_NO_BREAK_SPACE}` + this.title;
      },
    },
    {
      name: 'companions.inward.parents',
      title: stepCompanionsTitles.getParentCompanionsLabel,
      messageBoxError: function(_, { t }: FormStepperItemCallbackOptions) {
        return `${t('generic.inward')}:${U_NO_BREAK_SPACE}` + this.title;
      },
    },
    {
      name: 'companions.inward.travelling',
      title: stepCompanionsTitles.getTravellingCompanionsLabel,
      messageBoxError: function(_, { t }: FormStepperItemCallbackOptions) {
        return `${t('generic.inward')}:${U_NO_BREAK_SPACE}` + this.title;
      },
    },
    {
      name: 'companions.assistiveDevices',
      title: 'pages.tripBooking.forms.items.companions.devices.label',
    },
    {
      name: 'companions.otherDevice',
      title: 'pages.tripBooking.forms.items.companions.devices.other.label',
    },
  ],
});

export const FORM_STEPS: DatedFormSteps<true> = {
  [DatedStepName.TypeAndDate]: getStepOneItems(),
  [DatedStepName.Address]: getAddressStepperItems(),
  [DatedStepName.Time]: getTimeStepperItems(),
  [DatedStepName.AssistiveDevices]: getDevicesStepperItems(),
  [DatedStepName.Companions]: getCompanionsStepperItems(),
  [DatedStepName.Review]: { title: 'pages.tripBooking.forms.dated.stepTitles.review', items: [] },
};

/**
 * Création d'un objet complet représentant toutes les valeurs du formulaire à partir d'un objet
 * partiel passé en paramètres. Les propriétés non changées possèdent alors des valeurs par défaut.
 */
export function toAllFormValues(values: Partial<DatedBookingFormValues> = {}): DatedBookingFormValues {
  const defaultFormValues: DatedBookingFormValues = {
    date: '',
    tripType: '',
    requestedTime: {
      outward: {
        type: TRIP_BOOKING_TIME_TYPE_OUTWARD_DEFAULT,
        time: '',
      },
      inward: {
        type: TRIP_BOOKING_TIME_TYPE_INWARD_DEFAULT,
      },
    },
    assistiveDevices: {
      outward: null,
      inward: null,
    },
    originAddress: {
      address: '',
      apartment: '',
      driverNote: null,
      originalInstruction: null,
    },
    destinationAddress: {
      address: '',
      apartment: '',
      driverNote: null,
      originalInstruction: null,
    },
    dialysis: null,
    companions: {
      inward: getEmptyCompanions(),
      outward: getEmptyCompanions(),
      assistiveDevices: [],
      modificationDevices: [],
      otherDevice: '',

    },
    roundTripSameDevices: 'true',
    roundTripSameCompanions: 'true',
  };

  return Object.assign(defaultFormValues, values);
}

export const DATED_FORM_LOADER_CONTEXT = Symbol();

export const injectFormLoaderContext = () => inject<DatedFormLoaderContext>(DATED_FORM_LOADER_CONTEXT, {
  addLeg: () => {},
});
